import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Brain, FileDown, Loader2, CheckCircle2, AlertTriangle, Scale,
  Building2, Calendar, TrendingUp, Mail, Phone, Globe, Shield,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const formatEur = (v) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v || 0);

const CAT_BADGE = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-blue-500 text-white',
  C: 'bg-amber-500 text-white',
  D: 'bg-orange-500 text-white',
  E: 'bg-slate-500 text-white',
  X: 'bg-purple-500 text-white',
};

export default function InformePublicoHipoteca() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAceptarForm, setShowAceptarForm] = useState(false);
  const [aceptando, setAceptando] = useState(false);
  const [form, setForm] = useState({ nombre: '', dni: '', email: '', telefono: '', acepta: false });

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/hipotecas/publico/${token}`);
        setData(r.data);
        setForm((f) => ({ ...f, nombre: r.data.cliente?.nombre || '' }));
      } catch (e) {
        setError(e.response?.data?.detail || 'No se pudo cargar el informe');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const aceptar = async () => {
    if (!form.nombre.trim()) {
      toast({ title: 'Por favor introduce tu nombre completo', variant: 'destructive' });
      return;
    }
    if (!form.acepta) {
      toast({ title: 'Debes aceptar la propuesta para continuar', variant: 'destructive' });
      return;
    }
    setAceptando(true);
    try {
      await axios.post(`${API}/api/hipotecas/publico/${token}/aceptar-encargo`, form);
      const r = await axios.get(`${API}/api/hipotecas/publico/${token}`);
      setData(r.data);
      setShowAceptarForm(false);
      toast({ title: '¡Gracias!', description: 'Hemos recibido tu aceptación. El despacho se pondrá en contacto contigo.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAceptando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-purple-50">
        <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-red-50 p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Enlace no disponible</h1>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-4">Por favor contacta con el despacho para solicitar un nuevo enlace.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { despacho, cliente, hipoteca, resumen, clausulas, aceptado, aceptado_at } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-emerald-50">
      {/* Cabecera del despacho */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {despacho?.logo_url ? (
              <img src={despacho.logo_url} alt="" className="h-12 w-auto object-contain" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h2 className="font-bold text-lg text-gray-900">{despacho?.nombre || 'Despacho'}</h2>
              <div className="flex gap-3 text-xs text-gray-500 flex-wrap">
                {despacho?.email && <span><Mail className="w-3 h-3 inline mr-1" />{despacho.email}</span>}
                {despacho?.telefono && <span><Phone className="w-3 h-3 inline mr-1" />{despacho.telefono}</span>}
                {despacho?.web && <span><Globe className="w-3 h-3 inline mr-1" />{despacho.web}</span>}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(`${API}/api/hipotecas/publico/${token}/informe.pdf`, '_blank')}
            data-testid="btn-descargar-pdf-publico"
          >
            <FileDown className="w-4 h-4 mr-2" />Descargar informe PDF
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="text-center">
          <Badge className="mb-3 bg-purple-100 text-purple-800 border-purple-200">
            <Brain className="w-3 h-3 mr-1" />Análisis automatizado con IA
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Estimado/a {cliente?.nombre || 'cliente'},
          </h1>
          <p className="text-lg text-gray-600 mt-3 max-w-2xl mx-auto">
            Hemos analizado su escritura de préstamo hipotecario con <strong>{hipoteca?.banco || 'su entidad bancaria'}</strong> y
            hemos detectado <strong className="text-purple-700">{resumen.num_clausulas} cláusulas potencialmente abusivas</strong>{' '}
            con un importe recuperable estimado.
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpis">
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold tracking-wide text-purple-600 uppercase">Cláusulas detectadas</p>
              <p className="text-5xl font-bold text-purple-900 my-2">{resumen.num_clausulas}</p>
              <p className="text-xs text-purple-700">{resumen.num_alta_viabilidad} con alta viabilidad (≥70%)</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50 md:scale-105 shadow-lg">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold tracking-wide text-emerald-600 uppercase">Importe recuperable estimado</p>
              <p className="text-5xl font-bold text-emerald-900 my-2">{formatEur(resumen.importe_recuperable)}</p>
              <p className="text-xs text-emerald-700">+ {formatEur(resumen.importe_con_intereses - resumen.importe_recuperable)} intereses</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold tracking-wide text-blue-600 uppercase">Total con intereses legales</p>
              <p className="text-5xl font-bold text-blue-900 my-2">{formatEur(resumen.importe_con_intereses)}</p>
              <p className="text-xs text-blue-700"><TrendingUp className="w-3 h-3 inline mr-1" />Estimación máxima</p>
            </CardContent>
          </Card>
        </div>

        {/* Datos hipoteca */}
        <Card data-testid="datos-hipoteca">
          <CardContent className="p-6">
            <h2 className="font-bold text-lg mb-4 text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />Datos de su préstamo
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Entidad" value={hipoteca?.banco} />
              <Field label="Fecha firma" value={hipoteca?.fecha_firma} icon={<Calendar className="w-3 h-3" />} />
              <Field label="Capital" value={hipoteca?.capital_eur ? formatEur(hipoteca.capital_eur) : null} />
              <Field label="Plazo" value={hipoteca?.plazo_anios ? `${hipoteca.plazo_anios} años` : null} />
              <Field label="Tipo interés" value={hipoteca?.tipo_interes} />
              <Field label="Índice" value={hipoteca?.indice_referencia} />
              <Field label="Diferencial" value={hipoteca?.diferencial ? `${hipoteca.diferencial}%` : null} />
              <Field label="Régimen" value={resumen?.regimen} />
            </div>
          </CardContent>
        </Card>

        {/* Cláusulas */}
        <div>
          <h2 className="font-bold text-xl mb-4 text-gray-900">Cláusulas detectadas en su escritura</h2>
          <div className="space-y-3">
            {clausulas.map((c, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-md transition" data-testid={`clausula-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {c.codigo && <Badge className="font-mono">{c.codigo}</Badge>}
                        <Badge className={CAT_BADGE[c.categoria] || 'bg-gray-500 text-white'}>
                          {c.viabilidad_pct}% viabilidad
                        </Badge>
                        <h3 className="font-bold text-gray-900">{c.nombre}</h3>
                      </div>
                      {c.descripcion_breve && (
                        <p className="text-sm text-gray-700 mb-2">{c.descripcion_breve}</p>
                      )}
                      {c.jurisprudencia && (
                        <p className="text-xs text-blue-700">
                          <Scale className="w-3 h-3 inline mr-1" />
                          {c.jurisprudencia}
                        </p>
                      )}
                    </div>
                    {c.impacto_eur > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Impacto económico</p>
                        <p className="text-xl font-bold text-emerald-700">{formatEur(c.impacto_eur)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA: Aceptar encargo */}
        {aceptado ? (
          <Card className="bg-emerald-50 border-emerald-300 border-2" data-testid="ya-aceptado">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-900 mb-2">Hemos recibido tu aceptación</h2>
              <p className="text-emerald-800">
                Aceptado el {new Date(aceptado_at).toLocaleString('es-ES')}. El despacho se pondrá en contacto contigo en breve para los siguientes pasos.
              </p>
            </CardContent>
          </Card>
        ) : !showAceptarForm ? (
          <Card className="bg-gradient-to-br from-purple-600 to-blue-700 text-white border-0" data-testid="cta-aceptar">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">¿Quiere reclamar las cláusulas abusivas?</h2>
              <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
                Si acepta nuestro encargo profesional, prepararemos una reclamación extrajudicial al banco
                para recuperar hasta <strong>{formatEur(resumen.importe_con_intereses)}</strong>.
                Sin compromiso inicial.
              </p>
              <Button
                size="lg"
                onClick={() => setShowAceptarForm(true)}
                className="bg-white text-purple-700 hover:bg-purple-50 font-bold text-lg px-8 h-12"
                data-testid="btn-aceptar-encargo"
              >
                Aceptar encargo y empezar la reclamación
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-purple-300" data-testid="form-aceptar">
            <CardContent className="p-6">
              <h2 className="font-bold text-xl mb-1">Confirma tus datos</h2>
              <p className="text-sm text-gray-600 mb-5">Para tramitar tu encargo de forma profesional necesitamos confirmar tus datos.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre completo *</Label>
                  <Input
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    data-testid="input-firma-nombre"
                  />
                </div>
                <div>
                  <Label>DNI / NIE</Label>
                  <Input
                    value={form.dni}
                    onChange={(e) => setForm({ ...form, dni: e.target.value })}
                    data-testid="input-firma-dni"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="input-firma-email"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    data-testid="input-firma-telefono"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-start gap-2 bg-purple-50 p-4 rounded-lg">
                <Checkbox
                  id="acepta"
                  checked={form.acepta}
                  onCheckedChange={(v) => setForm({ ...form, acepta: !!v })}
                  data-testid="checkbox-acepta"
                />
                <label htmlFor="acepta" className="text-sm text-gray-700 leading-relaxed">
                  Acepto la propuesta de encargo profesional para que <strong>{despacho?.nombre || 'el despacho'}</strong> prepare y gestione
                  una reclamación extrajudicial frente a <strong>{hipoteca?.banco || 'la entidad bancaria'}</strong> respecto
                  a las cláusulas detectadas en este análisis. Entiendo que los importes son estimativos y que el despacho me contactará para formalizar el encargo.
                </label>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowAceptarForm(false)}>Cancelar</Button>
                <Button onClick={aceptar} disabled={aceptando || !form.acepta || !form.nombre.trim()} data-testid="btn-confirmar-aceptar">
                  {aceptando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Confirmar aceptación
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 pt-8 pb-4 flex items-center justify-center gap-2">
          <Shield className="w-3 h-3" />
          <span>Documento confidencial generado por {despacho?.nombre || 'el despacho'} con análisis IA automatizado. Los importes son estimaciones y no constituyen oferta vinculante.</span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <p className="text-xs text-gray-500 flex items-center gap-1">{icon}{label}</p>
      <p className="font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}
